/**
 * Graph Engine WebSocket service for real-time progress updates
 * Connects to /api/v2/ws/progress endpoint
 */

class GraphEngineWebSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.progressHandlers = [];
    this.connectionHandlers = [];
    this.disconnectionHandlers = [];
    this.errorHandlers = [];
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectTimeout = null;
    this.currentSessionId = null;
  }

  /**
   * Connect to Graph Engine WebSocket server
   * @param {string} baseUrl - Base URL (e.g., 'ws://localhost:8000')
   * @param {string} sessionId - Optional session ID for session-specific updates
   * @returns {Promise} - Resolves when connected, rejects on error
   */
  connect(baseUrl = 'ws://localhost:8000', sessionId = null) {
    return new Promise((resolve, reject) => {
      try {
        // Close existing connection if any
        if (this.socket) {
          this.socket.close();
        }

        // Build WebSocket URL - ensure we don't double up on paths
        const cleanBaseUrl = baseUrl.replace(/\/ws$/, ''); // Remove trailing /ws if present
        const wsUrl = sessionId 
          ? `${cleanBaseUrl}/api/v2/ws/progress/${sessionId}`
          : `${cleanBaseUrl}/api/v2/ws/progress`;

        console.log('Graph Engine WebSocket URL construction:');
        console.log('  Original baseUrl:', baseUrl);
        console.log('  Cleaned baseUrl:', cleanBaseUrl);
        console.log('  Final wsUrl:', wsUrl);
        console.log('  SessionId:', sessionId);
        this.currentSessionId = sessionId;

        // Create new WebSocket connection
        this.socket = new WebSocket(wsUrl);

        // Set up event handlers
        this.socket.onopen = () => {
          console.log('Graph Engine WebSocket connected');
          this.isConnected = true;
          this.reconnectAttempts = 0;
          
          // Send subscription message if needed
          if (sessionId) {
            this.subscribeToSession(sessionId);
          } else {
            this.subscribeToProgress();
          }
          
          this.connectionHandlers.forEach(handler => handler());
          resolve();
        };

        this.socket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('Graph Engine WebSocket message received:', data);
            
            // Handle different message types
            if (data.type === 'progress_update') {
              this.progressHandlers.forEach(handler => handler(data));
            } else if (data.type === 'agent_status') {
              this.progressHandlers.forEach(handler => handler(data));
            } else if (data.type === 'research_complete') {
              this.progressHandlers.forEach(handler => handler(data));
            } else if (data.type === 'error') {
              console.error('Graph Engine WebSocket error message:', data);
              this.errorHandlers.forEach(handler => handler(data));
            } else {
              // Generic message handling
              this.progressHandlers.forEach(handler => handler(data));
            }
          } catch (error) {
            console.error('Error parsing Graph Engine WebSocket message:', error);
          }
        };

        this.socket.onclose = (event) => {
          console.log('Graph Engine WebSocket disconnected:', event.code, event.reason);
          this.isConnected = false;
          this.disconnectionHandlers.forEach(handler => handler(event));
          
          // Attempt to reconnect if not closed cleanly
          if (event.code !== 1000 && event.code !== 1001) {
            this.attemptReconnect(baseUrl, sessionId);
          }
        };

        this.socket.onerror = (error) => {
          console.error('Graph Engine WebSocket error:', error);
          this.errorHandlers.forEach(handler => handler(error));
          reject(error);
        };
      } catch (error) {
        console.error('Error connecting to Graph Engine WebSocket:', error);
        reject(error);
      }
    });
  }

  /**
   * Attempt to reconnect to Graph Engine WebSocket server
   * @param {string} baseUrl - Base URL
   * @param {string} sessionId - Session ID
   */
  attemptReconnect(baseUrl, sessionId) {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
      
      console.log(`Attempting to reconnect to Graph Engine WebSocket in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
      
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = setTimeout(() => {
        console.log(`Reconnecting to Graph Engine WebSocket (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
        this.connect(baseUrl, sessionId).catch(() => {
          // If reconnect fails, it will trigger onclose which will attempt again
        });
      }, delay);
    } else {
      console.error('Maximum Graph Engine WebSocket reconnect attempts reached');
    }
  }

  /**
   * Disconnect from Graph Engine WebSocket server
   */
  disconnect() {
    if (this.socket) {
      this.socket.close(1000, 'Client disconnected');
      this.socket = null;
      this.isConnected = false;
      this.currentSessionId = null;
      clearTimeout(this.reconnectTimeout);
    }
  }

  /**
   * Send message to Graph Engine WebSocket server
   * @param {object} data - Message data
   * @returns {boolean} - True if sent, false otherwise
   */
  send(data) {
    if (this.socket && this.isConnected) {
      this.socket.send(JSON.stringify(data));
      return true;
    }
    return false;
  }

  /**
   * Subscribe to session-specific progress updates
   * @param {string} sessionId - Session ID
   * @returns {boolean} - True if sent, false otherwise
   */
  subscribeToSession(sessionId) {
    return this.send({
      type: 'subscribe',
      session_id: sessionId
    });
  }

  /**
   * Subscribe to general progress updates
   * @returns {boolean} - True if sent, false otherwise
   */
  subscribeToProgress() {
    return this.send({
      type: 'subscribe_progress'
    });
  }

  /**
   * Add progress update handler
   * @param {function} handler - Progress handler function
   */
  onProgress(handler) {
    this.progressHandlers.push(handler);
  }

  /**
   * Remove progress update handler
   * @param {function} handler - Progress handler function to remove
   */
  offProgress(handler) {
    this.progressHandlers = this.progressHandlers.filter(h => h !== handler);
  }

  /**
   * Add connection handler
   * @param {function} handler - Connection handler function
   */
  onConnect(handler) {
    this.connectionHandlers.push(handler);
  }

  /**
   * Remove connection handler
   * @param {function} handler - Connection handler function to remove
   */
  offConnect(handler) {
    this.connectionHandlers = this.connectionHandlers.filter(h => h !== handler);
  }

  /**
   * Add disconnection handler
   * @param {function} handler - Disconnection handler function
   */
  onDisconnect(handler) {
    this.disconnectionHandlers.push(handler);
  }

  /**
   * Remove disconnection handler
   * @param {function} handler - Disconnection handler function to remove
   */
  offDisconnect(handler) {
    this.disconnectionHandlers = this.disconnectionHandlers.filter(h => h !== handler);
  }

  /**
   * Add error handler
   * @param {function} handler - Error handler function
   */
  onError(handler) {
    this.errorHandlers.push(handler);
  }

  /**
   * Remove error handler
   * @param {function} handler - Error handler function to remove
   */
  offError(handler) {
    this.errorHandlers = this.errorHandlers.filter(h => h !== handler);
  }

  /**
   * Get connection status
   * @returns {boolean} - True if connected
   */
  getConnectionStatus() {
    return this.isConnected;
  }

  /**
   * Get current session ID
   * @returns {string|null} - Current session ID or null
   */
  getCurrentSessionId() {
    return this.currentSessionId;
  }
}

// Create singleton instance
const graphEngineWebSocketService = new GraphEngineWebSocketService();

export default graphEngineWebSocketService;